import {
  Box,
  Button,
  Divider,
  lighten,
  Stack,
  Typography,
} from "@mui/material";
import { useIsMobile } from "src/hooks/useIsMobile";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { useCallback, useMemo, useState } from "react";
import TermsOfConditions from "../TermsOfConditions";
import { usePrices } from "src/context/PricesProvider";
import { Coupon, ProductPrice } from "src/api/types";
import { useProduct } from "src/context/ProductProvider";
import { useTranslation } from "react-i18next";
import { theme } from "src/assets/theme";
import { useGetLatestWeightQuery } from "src/api/services/weightTrackerApi";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import ApplyCoupon from "../ApplyCoupon";
import ReviewsCarousel from "../ReviewsCarousel";
import Header from "../Headers/Header";
import Subheader from "../Headers/Subheader";
import Product from "../Product";
import PaymentProcessor from "../PaymentProcessor";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import ProductsDisplay from "src/components/ProductsDisplay";
import { productInformationArray } from "src/constants/producttInformation";

const PaymentInformation = () => {
  const isMobile = useIsMobile();
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const products = usePrices();
  const [selectedProduct, setSelectedProduct] = useSelectedProduct();
  const productSlug = useProduct((p) => p.slug);
  const page = "payment-information";
  const { t } = useTranslation();
  const { data: patientWeight } = useGetLatestWeightQuery();
  const isGlp1 = useProduct((product) => product.is_glp1);
  const category = useProduct((product) => product.category.name);
  const slug = useProduct((product) => product.slug);

  const weightLoss = useMemo(() => category === "Weight Loss", [category]);

  const handleProductChange = useCallback(
    (product: ProductPrice) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );

  const isQuarterly = useMemo(() => {
    if (selectedProduct == null) return false;

    return selectedProduct.number_of_months === 3;
  }, [selectedProduct]);

  const discountedPrice = useMemo(() => {
    if (selectedProduct == null) return "0";
    const discount = selectedProduct?.coupon?.amount ?? 0;

    return (selectedProduct.amount - discount).toLocaleString();
  }, [selectedProduct]);

  const withCouponPrice = useMemo(() => {
    if (selectedProduct == null) return "0";
    const discount = selectedProduct?.coupon?.amount ?? 0;
    const additionalDiscount = coupon?.amount ?? 0;

    const price = Math.max(
      selectedProduct?.amount - additionalDiscount - discount,
      selectedProduct.amount_limit ?? 0,
    );

    return price.toLocaleString();
  }, [coupon?.amount, selectedProduct]);

  const defaultProductPrice = useMemo(() => {
    const defaultProd = products.reduce(
      (min, p) => (p.interval_count < min.interval_count ? p : min),
      products[0],
    );

    if (defaultProd == null) {
      return "0";
    }

    return (
      isQuarterly ? defaultProd.amount * 3 : defaultProd.amount
    ).toLocaleString();
  }, [products, isQuarterly]);

  const subtotal = useMemo(
    () => (weightLoss ? defaultProductPrice : selectedProduct?.amount),
    [defaultProductPrice, selectedProduct?.amount, weightLoss],
  );

  const firstMonthSaving = useMemo(() => {
    const defaultPrice = Number(defaultProductPrice.replace(",", ""));
    const discountPrice = Number(discountedPrice.replace(",", ""));

    return defaultPrice - discountPrice;
  }, [defaultProductPrice, discountedPrice]);

  const saving = useMemo(() => {
    const additionalDiscount = coupon?.amount ?? 0;

    if (slug === "fhl") {
      const savings = isQuarterly ? 15 : 30;
      return savings + additionalDiscount;
    }

    return firstMonthSaving + additionalDiscount;
  }, [coupon?.amount, firstMonthSaving, isQuarterly, slug]);

  const buttonText = useMemo(() => t(`${page}.submit`), [t]);

  const goalWeight = useMemo(() => {
    if (patientWeight?.goal == null) return null;

    return patientWeight.goal;
  }, [patientWeight?.goal]);

  const informationArray = productInformationArray[slug];

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product isQuarterly={isQuarterly} />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          {informationArray.map((i, idx) => (
            <Box key={idx} display="flex" gap={1} alignItems="center">
              <CheckIcon
                sx={{ color: theme.palette.primary.main }}
                fontSize="small"
              />
              <Typography variant={isMobile ? "body2" : "body1"}>
                {t(i, { estimatedLoss: goalWeight })}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
      <Stack gap={3}>
        <ProductsDisplay
          handleProductChange={handleProductChange}
          page={page}
          products={products}
          selectedProduct={selectedProduct}
          discountedPrice={discountedPrice}
        />
        <ApplyCoupon setCoupon={setCoupon} coupon={null} />
        <Stack gap={4}>
          <Stack gap={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography fontSize={"15px"}>
                {t(`${page}.subtotal`, "Subtotal")}
              </Typography>

              <Typography fontSize={"15px"}>${subtotal}</Typography>
            </Stack>
            {weightLoss && firstMonthSaving > 0 ? (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  component={Stack}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  fontSize={"15px"}
                  color="success"
                >
                  {t(`${page}.firstMonthDiscount`)} <LocalOfferIcon />
                </Typography>
                <Typography fontSize={"15px"} color="success">
                  -${firstMonthSaving}
                </Typography>
              </Stack>
            ) : null}

            {coupon ? (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  component={Stack}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  fontSize={"15px"}
                  color="success"
                >
                  Coupon <LocalOfferIcon />
                </Typography>

                <Typography fontSize={"15px"} color="success">
                  -${coupon.amount}
                </Typography>
              </Stack>
            ) : null}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography fontSize={"15px"}>
                {t(`${page}.freeShipping`, "Shipping")}
              </Typography>
              <Typography fontSize={"15px"}>
                {t(`${page}.free`, "Free")}
              </Typography>
            </Stack>
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack>
                <Typography fontSize={"18px"}>
                  {t(`${page}.totalIfPrescribed`)}
                </Typography>
                {!weightLoss ? (
                  <Typography
                    color={theme.palette.primary.main}
                    fontStyle="italic"
                    fontSize={"18px"}
                  >
                    {t(
                      `${page}.total-disclaimer`,
                      "No extra charges. Just your treatment.",
                    )}
                  </Typography>
                ) : null}
              </Stack>
              <Typography fontSize={"18px"} data-testid="total-price">
                ${withCouponPrice}
              </Typography>
            </Stack>

            <Button
              variant="text"
              sx={{
                backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                boxShadow: " 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: 700,
                borderRadius: "10px",
                textAlign: "center",
              }}
              onClick={() => handleFocusIntoView("payment-input")}
            >
              {t(`${page}.totalSavings`, {
                saving,
              })}
            </Button>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={"20px"} fontWeight={700}>
                {t(`${page}.dueToday`)}
              </Typography>
              <Typography fontSize={"20px"} fontWeight={700}>
                $0
              </Typography>
            </Stack>
            {weightLoss ? (
              <ReviewsCarousel onFocus={handleFocusIntoView} />
            ) : null}
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={coupon}
              button={buttonText}
              dueToday={0}
            />
          ) : null}
        </Stack>
      </Stack>
      <TermsOfConditions
        page={page}
        productSlug={productSlug}
        isGlp1={isGlp1}
        numberOfMonths={selectedProduct ? selectedProduct.number_of_months : 1}
      />
    </Stack>
  );
};

export default PaymentInformation;
